/**
 * useAblyChannel.js — PRODUCTION v3
 * Place at: src/hooks/useAblyChannel.js
 *
 * BUGS FIXED vs v2:
 *
 *   BUG 1 — detach() called without checking channel state
 *     In React Strict Mode, cleanup runs immediately after first mount before
 *     the channel finishes attaching. Calling detach() on an attaching/detached
 *     channel throws: "Unable to attach; reason unknown; state = detached".
 *     Fix: only call detach() when channel state is "attached" or "attaching".
 *
 *   BUG 2 — import.meta.env.DEV used in a CRA app
 *     CRA does not support import.meta.env — it's Vite-only.
 *     Replaced with process.env.NODE_ENV === "development".
 *
 * ALL v2 FIXES RETAINED:
 *   - Singleton reset to null after close() (BUG 1 from v2)
 *   - Lazy connect (autoConnect: false)
 *   - TCP connection closed when no channels remain
 */

import { useEffect, useRef, useCallback } from "react";
import Ably from "ably";

// ─── SINGLETON ─────────────────────────────────────────────────────────────────
let ablyClient = null;

function getAblyClient() {
  if (!ablyClient) {
    const key = process.env.REACT_APP_ABLY_KEY;
    if (!key) {
      if (process.env.NODE_ENV === "development") { // BUG 2 FIX: was import.meta.env.DEV
        console.error("[Ably] REACT_APP_ABLY_KEY is not set — real-time comments disabled");
      }
      return null;
    }
    ablyClient = new Ably.Realtime({
      key,
      autoConnect:              false,
      disconnectedRetryTimeout: 2000,
      suspendedRetryTimeout:    30000,
    });
  }
  return ablyClient;
}

/**
 * Subscribe to an Ably channel lazily.
 *
 * @param {string}   channelName  — e.g. "comments:postId"
 * @param {function} onMessage    — callback(message) where message.data is the payload
 * @param {boolean}  enabled      — only connect when true (default true)
 * @returns {{ connected: boolean }}
 */
export function useAblyChannel(channelName, onMessage, enabled = true) {
  const channelRef   = useRef(null);
  const onMessageRef = useRef(onMessage);
  const connectedRef = useRef(false);

  // Keep latest callback without triggering reconnect
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const subscribe = useCallback(() => {
    const client = getAblyClient();
    if (!client || !channelName) return;

    const state = client.connection.state;
    if (state === "initialized" || state === "suspended" || state === "closed") {
      client.connect();
    }

    const channel = client.channels.get(channelName);
    channelRef.current = channel;

    channel.subscribe((message) => {
      onMessageRef.current?.(message);
    });

    connectedRef.current = true;
  }, [channelName]);

  const unsubscribe = useCallback(() => {
    const channel = channelRef.current;
    if (channel) {
      channel.unsubscribe();
      // Do NOT call channel.detach() — Ably throws an internal event error
      // ("Unable to attach; reason unknown; state = detached") when detach()
      // is called on a channel that hasn't finished attaching yet (React Strict
      // Mode fires cleanup before attach completes). The error is emitted via
      // Ably's EventEmitter so try/catch cannot suppress it.
      // unsubscribe() removes our listener. client.close() below tears down
      // the TCP connection entirely — detach() is redundant.

      channelRef.current = null;
    }
    connectedRef.current = false;

    // Close TCP connection if no other channels remain active
    const client = getAblyClient();
    if (client) {
      const active = Object.values(client.channels.all || {})
        .filter(ch => ch.state === "attached" || ch.state === "attaching");
      if (active.length === 0) {
        client.close();
        ablyClient = null; // reset singleton so next subscribe() gets a fresh client
      }
    }
  }, []);

  useEffect(() => {
    if (enabled && channelName) {
      subscribe();
    } else {
      unsubscribe();
    }
    return () => {
      unsubscribe();
    };
  }, [enabled, channelName, subscribe, unsubscribe]);

  return { connected: connectedRef.current };
}